import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { DataTable, type Column } from '../../components/DataTable';
import { FormRow } from '../../components/ui/FormRow';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Input';
import { toast, confirmar } from '../../lib/toast';

type Proveedor = {
  id: string;
  nombre: string;
  tipo_producto: string | null;
  contacto_principal: string | null;
  email: string | null;
  telefono: string | null;
  pais: string | null;
  activo: boolean;
  foc_pct: number | null;
  production_time_dias: number | null;
  payment_deposit_pct: number | null;
  payment_transfer_pct: number | null;
  web: string | null;
  whatsapp: string | null;
  direccion: string | null;
  notas: string | null;
};

const empty = {
  nombre: '',
  tipo_producto: '',
  contacto_principal: '',
  email: '',
  telefono: '',
  whatsapp: '',
  web: '',
  direccion: '',
  pais: 'CN',
  foc_pct: '1',
  production_time_dias: '35',
  payment_deposit_pct: '10',
  payment_transfer_pct: '90',
  notas: '',
};

export function ProveedoresList() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const proveedores = useQuery({
    queryKey: ['proveedores', 'list-full'],
    queryFn: () => api.get<{ data: Proveedor[] }>('/proveedores?limit=1000'),
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [fusionOpen, setFusionOpen] = useState(false);
  const [masterId, setMasterId] = useState<string>('');
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [form, setForm] = useState<typeof empty>(empty);

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/proveedores', {
      ...form,
      foc_pct: form.foc_pct === '' ? null : Number(form.foc_pct),
      production_time_dias: form.production_time_dias === '' ? null : Number(form.production_time_dias),
      payment_deposit_pct: form.payment_deposit_pct === '' ? null : Number(form.payment_deposit_pct),
      payment_transfer_pct: form.payment_transfer_pct === '' ? null : Number(form.payment_transfer_pct),
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['proveedores'] });
      setNuevoOpen(false);
      setForm(empty);
      if (r?.id) navigate(`/proveedores/listado/${r.id}`);
    },
  });

  const fusionar = useMutation({
    mutationFn: (body: { master_id: string; ids: string[] }) =>
      api.post<{ ok: boolean; fusionados: number; moved: Record<string, number> }>('/proveedores/fusionar', body),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['proveedores'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      qc.invalidateQueries({ queryKey: ['proveedor'] });
      toast.success(
        'Fusión completada',
        `${r.fusionados} proveedor(es) absorbido(s). Movidos: ` +
          Object.entries(r.moved).map(([k, v]) => `${k}=${v}`).join(', '),
      );
      setFusionOpen(false);
      setMasterId('');
      setSelectedIds(new Set());
    },
    onError: (err) => toast.error('No se pudo completar la fusión', (err as Error).message),
  });

  const rows = proveedores.data?.data ?? [];

  const seleccionados = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const columns: Column<Proveedor>[] = [
    { key: 'nombre', label: 'Nombre', sortValue: (r) => r.nombre },
    { key: 'tipo_producto', label: 'Tipo producto', render: (r) => r.tipo_producto ?? '—', sortValue: (r) => r.tipo_producto ?? '' },
    { key: 'contacto_principal', label: 'Contacto', render: (r) => r.contacto_principal ?? '—', sortValue: (r) => r.contacto_principal ?? '' },
    { key: 'email', label: 'Email', render: (r) => r.email ?? '—', sortValue: (r) => r.email ?? '' },
    { key: 'telefono', label: 'Teléfono', render: (r) => r.telefono ?? '—', sortValue: (r) => r.telefono ?? '' },
    { key: 'pais', label: 'País', render: (r) => r.pais ?? '—', sortValue: (r) => r.pais ?? '' },
    { key: 'activo', label: 'Activo', render: (r) => r.activo ? 'Sí' : <span className="text-rose-600">No</span>, sortValue: (r) => (r.activo ? 1 : 0) },
  ];

  return (
    <div>
      <PageHeader
        title="Proveedores"
        subtitle="Fabricantes y suministradores (mayoritariamente China)."
        actions={
          <>
            {selectedIds.size >= 2 && (
              <Button variant="edit" onClick={() => { setMasterId(seleccionados[0]?.id ?? ''); setFusionOpen(true); }}>
                ⚙ Fusionar {selectedIds.size} proveedores
              </Button>
            )}
            <Button variant="create" onClick={() => setNuevoOpen(true)}>+ Nuevo proveedor</Button>
          </>
        }
      />

      {selectedIds.size > 0 && (
        <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          <strong>{selectedIds.size}</strong> proveedor(es) seleccionado(s).
          {selectedIds.size < 2 && ' (selecciona al menos 2 para poder fusionar)'}
          <button onClick={() => setSelectedIds(new Set())} className="ml-2 cursor-pointer text-xs underline">
            limpiar
          </button>
        </div>
      )}

      <DataTable<Proveedor>
        columns={columns}
        rows={rows}
        loading={proveedores.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/proveedores/listado/${r.id}`)}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        searchPlaceholder="Buscar por nombre, contacto, email…"
        defaultSort={{ key: 'nombre' }}
      />

      {/* ── Modal: nuevo proveedor ── */}
      <Modal
        open={nuevoOpen}
        onClose={() => { setNuevoOpen(false); setForm(empty); }}
        title="Nuevo proveedor"
        size="lg"
        footer={
          <>
            <Button variant="danger" onClick={() => { setNuevoOpen(false); setForm(empty); }}>Cancelar</Button>
            <Button variant="save" onClick={() => create.mutate()} disabled={!form.nombre.trim() || create.isPending}>
              {create.isPending ? 'Guardando…' : 'Crear y abrir ficha'}
            </Button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Nombre" required><Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></FormRow>
          <FormRow label="Tipo de producto"><Input value={form.tipo_producto} onChange={(e) => setForm((f) => ({ ...f, tipo_producto: e.target.value }))} placeholder="CARCASAS, HDD, USB, SSD…" /></FormRow>
          <FormRow label="Persona contacto"><Input value={form.contacto_principal} onChange={(e) => setForm((f) => ({ ...f, contacto_principal: e.target.value }))} /></FormRow>
          <FormRow label="Email"><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></FormRow>
          <FormRow label="Teléfono"><Input value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} /></FormRow>
          <FormRow label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} /></FormRow>
          <FormRow label="Web"><Input value={form.web} onChange={(e) => setForm((f) => ({ ...f, web: e.target.value }))} placeholder="https://…" /></FormRow>
          <FormRow label="País"><Input value={form.pais} onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value }))} /></FormRow>
          <div className="sm:col-span-2">
            <FormRow label="Dirección"><Textarea rows={2} value={form.direccion} onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))} /></FormRow>
          </div>
          <FormRow label="F.O.C. (%)"><Input type="number" value={form.foc_pct} onChange={(e) => setForm((f) => ({ ...f, foc_pct: e.target.value }))} /></FormRow>
          <FormRow label="Production time (días)"><Input type="number" value={form.production_time_dias} onChange={(e) => setForm((f) => ({ ...f, production_time_dias: e.target.value }))} /></FormRow>
          <FormRow label="Payment Deposit (%)"><Input type="number" value={form.payment_deposit_pct} onChange={(e) => setForm((f) => ({ ...f, payment_deposit_pct: e.target.value }))} /></FormRow>
          <FormRow label="Payment Transfer post-inspection (%)"><Input type="number" value={form.payment_transfer_pct} onChange={(e) => setForm((f) => ({ ...f, payment_transfer_pct: e.target.value }))} /></FormRow>
          <div className="sm:col-span-2">
            <FormRow label="Notas"><Textarea rows={2} value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} /></FormRow>
          </div>
        </div>
      </Modal>

      {/* ── Modal: fusionar proveedores ── */}
      <Modal
        open={fusionOpen}
        onClose={() => setFusionOpen(false)}
        title={`Fusionar ${selectedIds.size} proveedores`}
        size="lg"
        footer={
          <>
            <Button variant="danger" onClick={() => setFusionOpen(false)}>Cancelar</Button>
            <Button
              variant="save"
              onClick={async () => {
                if (!(await confirmar({ title: '¿Fusionar proveedores?', message: 'Los proveedores no-master quedarán soft-deleted y sus productos / pedidos / contactos pasarán al master.', confirmLabel: 'Fusionar', danger: true }))) return;
                const ids = Array.from(selectedIds).filter((id) => id !== masterId);
                fusionar.mutate({ master_id: masterId, ids });
              }}
              disabled={!masterId || fusionar.isPending}
            >
              {fusionar.isPending ? 'Fusionando…' : 'Fusionar al master'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Elige el proveedor que se mantendrá como <strong>MASTER</strong>. El resto se soft-deletean y
            sus productos / pedidos / contactos / notas / incidencias pasan al master. Los campos vacíos
            del master se rellenan con valores del primer fusionado que los tenga.
          </p>
          <FormRow label="Master" required>
            <Select
              options={seleccionados.map((p) => ({ value: p.id, label: `${p.nombre}${p.tipo_producto ? ` · ${p.tipo_producto}` : ''}` }))}
              value={masterId}
              emptyLabel="— elegir —"
              onChange={(e) => setMasterId(e.target.value)}
            />
          </FormRow>
          <Card title="Proveedores seleccionados">
            <ul className="space-y-1 text-sm">
              {seleccionados.map((p) => (
                <li key={p.id} className="flex items-center justify-between">
                  <span>{p.nombre} <span className="text-xs text-slate-500">({p.tipo_producto ?? '—'})</span></span>
                  {p.id === masterId ? (
                    <span className="text-xs font-semibold text-emerald-700">★ MASTER</span>
                  ) : (
                    <span className="text-xs text-rose-600">se fusionará</span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Modal>
    </div>
  );
}
